/*
 * VaultPasscodeResetPage
 * ----------------------
 * Two-phase passcode-recovery page (Prompt 34).
 *   Phase 1 — Request:  /vault-passcode-reset/:qr_code
 *     Email input. Submitting always returns the same friendly success
 *     screen, whether or not the email is on file (privacy-preserving,
 *     same contract as /api/orders/request-link).
 *   Phase 2 — Reset:    /vault-passcode-reset/:qr_code?token=...
 *     New passcode + confirm + optional hint. On success, deep-links back
 *     to /vault-edit/:qr_code so the buyer can immediately unlock.
 *
 * Visual system: matches VaultEditPage / MyOrdersPage — cream page,
 * Cormorant Garamond headings, Georgia serif body, midnight-navy CTA, gold
 * accents. No layout deviations from the established Sent2ment style.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const C = {
  cream:     '#FDFCFA',
  midnight:  '#0B1F3A',
  gold:      '#C6A96B',
  text:      '#0B1F3A',
  textSec:   '#6B6259',
  border:    '#E5DED3',
};

export default function VaultPasscodeResetPage() {
  const { qr_code } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = (searchParams.get('token') || '').trim();
  const isResetPhase = Boolean(token);

  // Request-phase state
  const [email, setEmail] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  // Reset-phase state
  const [newPwd, setNewPwd] = useState('');
  const [newPwdConfirm, setNewPwdConfirm] = useState('');
  const [hint, setHint] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    document.title = 'Passcode recovery — Sent2ment';
  }, []);

  /* ── Phase 1 handler ── */
  const handleRequest = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setRequesting(true);
    try {
      await fetch(`${API_BASE}/api/memory/${encodeURIComponent(qr_code)}/request-passcode-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
    } catch {
      /* swallow — same privacy response regardless */
    } finally {
      setRequesting(false);
      setRequestSent(true);
    }
  };

  /* ── Phase 2 handler ── */
  const handleReset = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!newPwd) { setErrorMsg('Please enter a new passcode.'); return; }
    if (newPwd.length < 4) { setErrorMsg('Please choose at least 4 characters.'); return; }
    if (newPwd !== newPwdConfirm) { setErrorMsg('The passcodes don\u2019t match.'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/memory/${encodeURIComponent(qr_code)}/consume-passcode-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPwd, hint: hint || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        setErrorMsg(data.detail || 'Reset link is invalid or has expired.');
        return;
      }
      if (!res.ok) {
        setErrorMsg(data.detail || 'Could not save your new passcode. Please try again.');
        return;
      }
      setSuccess(true);
    } catch {
      setErrorMsg('Connection failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /* ── Layout wrapper ── */
  const wrapper = (children) => (
    <div className="min-h-screen py-24 px-6" style={{ background: C.cream }}>
      <div className="max-w-md mx-auto" data-testid="vault-passcode-reset-page">
        <div className="text-center mb-8">
          <p className="text-[11px] tracking-[0.3em] uppercase mb-3" style={{ color: C.gold }}>Passcode recovery</p>
          <h1 className="text-3xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}>
            A quiet way back.
          </h1>
        </div>
        <div className="p-8" style={{ background: '#FFFFFF', border: `1px solid ${C.border}` }}>
          {children}
        </div>
        <div className="mt-6 text-center">
          <Link to={`/vault-edit/${encodeURIComponent(qr_code || '')}`} className="text-xs italic" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
            Return to your Memory Vault
          </Link>
        </div>
      </div>
    </div>
  );

  /* ── Reset phase ── */
  if (isResetPhase) {
    if (success) {
      return wrapper(
        <div className="text-center" data-testid="reset-success">
          <h2 className="text-xl mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}>
            Your new passcode is in place.
          </h2>
          <p className="text-sm mb-6" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
            You can now return to your Memory Vault and unlock it with your new passcode.
          </p>
          <button
            type="button"
            data-testid="reset-success-continue"
            onClick={() => navigate(`/vault-edit/${encodeURIComponent(qr_code)}`)}
            className="text-xs uppercase tracking-[0.2em] px-8 py-3"
            style={{ background: C.gold, color: C.midnight, fontWeight: 600 }}
          >
            Continue
          </button>
        </div>
      );
    }
    return wrapper(
      <form onSubmit={handleReset} className="space-y-4">
        <div>
          <label className="block text-[11px] tracking-[0.2em] uppercase mb-2" style={{ color: C.textSec }}>New passcode</label>
          <input
            type="password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            data-testid="reset-new-passcode-input"
            autoComplete="new-password"
            className="w-full px-4 py-3 text-sm outline-none"
            style={{ color: C.text, background: '#FFFFFF', border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif' }}
          />
        </div>
        <div>
          <label className="block text-[11px] tracking-[0.2em] uppercase mb-2" style={{ color: C.textSec }}>Confirm passcode</label>
          <input
            type="password"
            value={newPwdConfirm}
            onChange={(e) => setNewPwdConfirm(e.target.value)}
            data-testid="reset-new-passcode-confirm"
            autoComplete="new-password"
            className="w-full px-4 py-3 text-sm outline-none"
            style={{ color: C.text, background: '#FFFFFF', border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif' }}
          />
        </div>
        <div>
          <label className="block text-[11px] tracking-[0.2em] uppercase mb-2" style={{ color: C.textSec }}>Optional hint</label>
          <input
            type="text"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            data-testid="reset-new-hint-input"
            className="w-full px-4 py-3 text-sm outline-none"
            style={{ color: C.text, background: '#FFFFFF', border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif' }}
          />
        </div>
        {errorMsg ? (
          <p className="text-xs" data-testid="reset-error" style={{ color: '#B04D3F', fontFamily: 'Georgia, serif' }}>
            {errorMsg}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={saving}
          data-testid="reset-submit-btn"
          className="w-full text-xs uppercase tracking-[0.2em] py-3 mt-2 disabled:opacity-60"
          style={{ background: C.midnight, color: C.gold, fontWeight: 600 }}
        >
          {saving ? 'Saving\u2026' : 'Set new passcode'}
        </button>
      </form>
    );
  }

  /* ── Request phase ── */
  if (requestSent) {
    return wrapper(
      <div className="text-center" data-testid="reset-request-sent">
        <h2 className="text-xl mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.midnight }}>
          Please check your inbox.
        </h2>
        <p className="text-sm" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
          If that email is on file for this Memory Vault, we&rsquo;ve just sent a recovery link.
          The link expires in 15 minutes and may only be used once.
        </p>
      </div>
    );
  }
  return wrapper(
    <form onSubmit={handleRequest} className="space-y-4">
      <p className="text-sm leading-relaxed" style={{ color: C.textSec, fontFamily: 'Georgia, serif' }}>
        Enter the email you used with this Memory Vault &mdash; whether you purchased it, were sent it, or saved your own access via the QR.
      </p>
      <div>
        <label className="block text-[11px] tracking-[0.2em] uppercase mb-2" style={{ color: C.textSec }}>Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="reset-request-email-input"
          autoComplete="email"
          className="w-full px-4 py-3 text-sm outline-none"
          style={{ color: C.text, background: '#FFFFFF', border: `1px solid ${C.border}`, fontFamily: 'Georgia, serif' }}
        />
      </div>
      <button
        type="submit"
        disabled={requesting}
        data-testid="reset-request-submit-btn"
        className="w-full text-xs uppercase tracking-[0.2em] py-3 mt-2 disabled:opacity-60"
        style={{ background: C.midnight, color: C.gold, fontWeight: 600 }}
      >
        {requesting ? 'Sending\u2026' : 'Send recovery link'}
      </button>
    </form>
  );
}
